export type UnicodeAlphabet = {
  uppercase: string[];
  lowercase: string[];
};

export type CursiveStyleDefinition = {
  id: string;
  name: string;
  description: string;
  previewLabel: string;
  alphabet: UnicodeAlphabet;
};

const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";

function createAlphabet(
  uppercaseCodePoints: number[],
  lowercaseCodePoints: number[],
): UnicodeAlphabet {
  return {
    uppercase: uppercaseCodePoints.map((codePoint) =>
      String.fromCodePoint(codePoint),
    ),
    lowercase: lowercaseCodePoints.map((codePoint) =>
      String.fromCodePoint(codePoint),
    ),
  };
}

const scriptAlphabet = createAlphabet(
  [
    0x1d49c, 0x212c, 0x1d49e, 0x1d49f, 0x2130, 0x2131, 0x1d4a2, 0x210b,
    0x2110, 0x1d4a5, 0x1d4a6, 0x2112, 0x2133, 0x1d4a9, 0x1d4aa, 0x1d4ab,
    0x1d4ac, 0x211b, 0x1d4ae, 0x1d4af, 0x1d4b0, 0x1d4b1, 0x1d4b2, 0x1d4b3,
    0x1d4b4, 0x1d4b5,
  ],
  [
    0x1d4b6, 0x1d4b7, 0x1d4b8, 0x1d4b9, 0x212f, 0x1d4bb, 0x210a, 0x1d4bd,
    0x1d4be, 0x1d4bf, 0x1d4c0, 0x1d4c1, 0x1d4c2, 0x1d4c3, 0x2134, 0x1d4c5,
    0x1d4c6, 0x1d4c7, 0x1d4c8, 0x1d4c9, 0x1d4ca, 0x1d4cb, 0x1d4cc, 0x1d4cd,
    0x1d4ce, 0x1d4cf,
  ],
);

const boldScriptAlphabet = createAlphabet(
  [
    0x1d4d0, 0x1d4d1, 0x1d4d2, 0x1d4d3, 0x1d4d4, 0x1d4d5, 0x1d4d6, 0x1d4d7,
    0x1d4d8, 0x1d4d9, 0x1d4da, 0x1d4db, 0x1d4dc, 0x1d4dd, 0x1d4de, 0x1d4df,
    0x1d4e0, 0x1d4e1, 0x1d4e2, 0x1d4e3, 0x1d4e4, 0x1d4e5, 0x1d4e6, 0x1d4e7,
    0x1d4e8, 0x1d4e9,
  ],
  [
    0x1d4ea, 0x1d4eb, 0x1d4ec, 0x1d4ed, 0x1d4ee, 0x1d4ef, 0x1d4f0, 0x1d4f1,
    0x1d4f2, 0x1d4f3, 0x1d4f4, 0x1d4f5, 0x1d4f6, 0x1d4f7, 0x1d4f8, 0x1d4f9,
    0x1d4fa, 0x1d4fb, 0x1d4fc, 0x1d4fd, 0x1d4fe, 0x1d4ff, 0x1d500, 0x1d501,
    0x1d502, 0x1d503,
  ],
);

const italicAlphabet = createAlphabet(
  [
    0x1d434, 0x1d435, 0x1d436, 0x1d437, 0x1d438, 0x1d439, 0x1d43a, 0x1d43b,
    0x1d43c, 0x1d43d, 0x1d43e, 0x1d43f, 0x1d440, 0x1d441, 0x1d442, 0x1d443,
    0x1d444, 0x1d445, 0x1d446, 0x1d447, 0x1d448, 0x1d449, 0x1d44a, 0x1d44b,
    0x1d44c, 0x1d44d,
  ],
  [
    0x1d44e, 0x1d44f, 0x1d450, 0x1d451, 0x1d452, 0x1d453, 0x1d454, 0x210e,
    0x1d456, 0x1d457, 0x1d458, 0x1d459, 0x1d45a, 0x1d45b, 0x1d45c, 0x1d45d,
    0x1d45e, 0x1d45f, 0x1d460, 0x1d461, 0x1d462, 0x1d463, 0x1d464, 0x1d465,
    0x1d466, 0x1d467,
  ],
);

const boldItalicAlphabet = createAlphabet(
  [
    0x1d468, 0x1d469, 0x1d46a, 0x1d46b, 0x1d46c, 0x1d46d, 0x1d46e, 0x1d46f,
    0x1d470, 0x1d471, 0x1d472, 0x1d473, 0x1d474, 0x1d475, 0x1d476, 0x1d477,
    0x1d478, 0x1d479, 0x1d47a, 0x1d47b, 0x1d47c, 0x1d47d, 0x1d47e, 0x1d47f,
    0x1d480, 0x1d481,
  ],
  [
    0x1d482, 0x1d483, 0x1d484, 0x1d485, 0x1d486, 0x1d487, 0x1d488, 0x1d489,
    0x1d48a, 0x1d48b, 0x1d48c, 0x1d48d, 0x1d48e, 0x1d48f, 0x1d490, 0x1d491,
    0x1d492, 0x1d493, 0x1d494, 0x1d495, 0x1d496, 0x1d497, 0x1d498, 0x1d499,
    0x1d49a, 0x1d49b,
  ],
);

const sansItalicAlphabet = createAlphabet(
  [
    0x1d608, 0x1d609, 0x1d60a, 0x1d60b, 0x1d60c, 0x1d60d, 0x1d60e, 0x1d60f,
    0x1d610, 0x1d611, 0x1d612, 0x1d613, 0x1d614, 0x1d615, 0x1d616, 0x1d617,
    0x1d618, 0x1d619, 0x1d61a, 0x1d61b, 0x1d61c, 0x1d61d, 0x1d61e, 0x1d61f,
    0x1d620, 0x1d621,
  ],
  [
    0x1d622, 0x1d623, 0x1d624, 0x1d625, 0x1d626, 0x1d627, 0x1d628, 0x1d629,
    0x1d62a, 0x1d62b, 0x1d62c, 0x1d62d, 0x1d62e, 0x1d62f, 0x1d630, 0x1d631,
    0x1d632, 0x1d633, 0x1d634, 0x1d635, 0x1d636, 0x1d637, 0x1d638, 0x1d639,
    0x1d63a, 0x1d63b,
  ],
);

const alternateScriptAlphabet = {
  uppercase: scriptAlphabet.uppercase.map((letter, index) =>
    index % 2 === 0 ? letter : boldScriptAlphabet.uppercase[index],
  ),
  lowercase: scriptAlphabet.lowercase.map((letter, index) =>
    index % 2 === 0 ? letter : boldScriptAlphabet.lowercase[index],
  ),
};

const signatureAlphabet = {
  uppercase: boldScriptAlphabet.uppercase,
  lowercase: scriptAlphabet.lowercase,
};

const decorativeAlphabet = {
  uppercase: boldScriptAlphabet.uppercase.map(
    (letter, index) => (index < 6 ? letter : boldItalicAlphabet.uppercase[index]),
  ),
  lowercase: boldItalicAlphabet.lowercase,
};

export const cursiveStyleDefinitions: CursiveStyleDefinition[] = [
  {
    id: "elegant-cursive",
    name: "Elegant Cursive",
    description: "A classic cursive script look for refined words and headings.",
    previewLabel: "Classic script style",
    alphabet: scriptAlphabet,
  },
  {
    id: "bold-cursive",
    name: "Bold Cursive",
    description: "A heavier cursive font generator style that stands out clearly.",
    previewLabel: "Bolder script look",
    alphabet: boldScriptAlphabet,
  },
  {
    id: "script-cursive",
    name: "Script Cursive",
    description: "A balanced cursive script generator style with extra contrast.",
    previewLabel: "Mixed script styling",
    alphabet: alternateScriptAlphabet,
  },
  {
    id: "signature-style",
    name: "Signature Style",
    description: "A signature-inspired cursive name generator output for names and initials.",
    previewLabel: "Signature-inspired look",
    alphabet: signatureAlphabet,
  },
  {
    id: "fancy-cursive",
    name: "Fancy Cursive",
    description: "A decorative generate cursive option for captions and creative copy.",
    previewLabel: "Decorative script style",
    alphabet: decorativeAlphabet,
  },
  {
    id: "simple-cursive",
    name: "Simple Cursive",
    description: "A simpler cursive font style with an easy-to-read handwritten feel.",
    previewLabel: "Light italic handwriting",
    alphabet: italicAlphabet,
  },
  {
    id: "handwriting-style",
    name: "Handwriting Style",
    description: "A smooth handwritten variation for notes, headings, and cursive words.",
    previewLabel: "Casual handwritten look",
    alphabet: sansItalicAlphabet,
  },
  {
    id: "decorative-cursive",
    name: "Decorative Cursive",
    description: "A more dramatic copy paste cursive option for standout text.",
    previewLabel: "High-contrast cursive style",
    alphabet: boldItalicAlphabet,
  },
];

function transformCharacter(character: string, alphabet: UnicodeAlphabet) {
  const uppercaseIndex = UPPERCASE.indexOf(character);
  if (uppercaseIndex >= 0) {
    return alphabet.uppercase[uppercaseIndex] || character;
  }

  const lowercaseIndex = LOWERCASE.indexOf(character);
  if (lowercaseIndex >= 0) {
    return alphabet.lowercase[lowercaseIndex] || character;
  }

  return character;
}

export function transformTextWithAlphabet(
  input: string,
  alphabet: UnicodeAlphabet,
) {
  return Array.from(input)
    .map((character) => transformCharacter(character, alphabet))
    .join("");
}

export function transformTextWithStyle(
  input: string,
  style: CursiveStyleDefinition,
) {
  return transformTextWithAlphabet(input, style.alphabet);
}
